<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("employees.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("employees.error") }}</div>
    <template v-else>
      <!-- header: title + crumb + top search + quick add -->
      <div class="row between">
        <div>
          <div class="pagetitle">{{ $t("employees.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("employees.title") }}</div>
        </div>
        <div class="searchbar grow">
          🔎
          <input
            type="text"
            v-model="q"
            :placeholder="$t('employees.searchPlaceholder')"
            @keyup.enter="reload"
          />
          <span class="k">Ctrl K</span>
        </div>
        <button class="btn primary" type="button" @click="goAdd">➕ {{ $t("employees.newEmployee") }}</button>
      </div>

      <!-- dual search: the NL assistant beside the regular filtered search -->
      <div class="panel mt">
        <div class="panel__head">
          <h4>🤖 {{ $t("employees.assistant.title") }}</h4>
          <span class="muted xs">{{ $t("employees.assistant.hint") }}</span>
        </div>
        <div class="panel__body">
          <div class="chat">
            <div v-if="chat.question" class="bubble me">{{ chat.question }}</div>
            <div v-if="chat.asking" class="bubble bot">{{ $t("employees.assistant.thinking") }}</div>
            <template v-else-if="chat.answer">
              <div class="bubble bot" data-test="assistant-answer">{{ chat.answer }}</div>
              <!-- When the answer came from the employee search, the list below
                   has already been filtered to match — say so, otherwise the
                   screen silently changing under the user is alarming. -->
              <div v-if="chat.appliedFilters" class="xs muted" data-test="assistant-applied">
                ⬇️ {{ $t("employees.assistant.applied") }}
              </div>
              <!-- The FAQ bank being empty is the single most common reason this
                   panel "doesn't work"; link to the screen that fills it. -->
              <router-link
                v-if="chat.emptyBank"
                class="xs"
                data-test="assistant-bank-link"
                :to="{ name: 'chatbot-qa' }"
              >
                {{ $t("employees.assistant.fillBank") }}
              </router-link>
            </template>
          </div>
          <div class="row" style="gap: 8px; margin-top: 10px">
            <div class="searchbar grow">
              🤖
              <input
                type="text"
                v-model="ask"
                :placeholder="$t('employees.assistant.placeholder')"
                @keyup.enter="askBot"
              />
            </div>
            <button class="btn accent" type="button" :disabled="chat.asking" @click="askBot">
              {{ $t("employees.assistant.send") }}
            </button>
          </div>
        </div>
      </div>

      <!-- regular filtered list -->
      <div class="panel mt">
        <div class="toolbar">
          <div class="searchbar grow">
            🔎
            <input
              type="text"
              v-model="q"
              :placeholder="$t('employees.searchPlaceholder')"
              @keyup.enter="reload"
            />
          </div>
          <!-- B3: the status chips + loose department select are now one
               dropdown. The toggle carries a count so a collapsed panel can
               never hide why the list looks short. -->
          <div class="filters" data-test="filters">
            <button
              class="btn soft sm"
              type="button"
              data-test="filters-toggle"
              :aria-expanded="String(filtersOpen)"
              @click.stop="filtersOpen = !filtersOpen"
            >
              ⚙️ {{ $t("employees.filter.button") }}<template v-if="filterCount"> ({{ fInt(filterCount) }})</template>
            </button>
            <div v-if="filtersOpen" class="filters__panel" data-test="filters-panel" @click.stop>
              <label class="filters__row">
                <span class="xs muted">{{ $t("employees.col.status") }}</span>
                <select class="select" v-model="status" data-test="status-filter" @change="reload">
                  <option v-for="c in statusChips" :key="c.value" :value="c.value">{{ c.label }}</option>
                </select>
              </label>
              <label class="filters__row">
                <span class="xs muted">{{ $t("employees.col.dept") }}</span>
                <select class="select" v-model="deptId" data-test="dept-filter" @change="reload">
                  <option value="">{{ $t("employees.allDepartments") }}</option>
                  <option v-for="d in departments" :key="d.id" :value="d.id">{{ deptLabel(d) }}</option>
                </select>
              </label>
              <div class="row between filters__foot">
                <button class="btn ghost sm" type="button" data-test="filters-clear" :disabled="!filterCount" @click="clearFilters">
                  {{ $t("employees.filter.clear") }}
                </button>
                <button class="btn soft sm" type="button" data-test="filters-done" @click="filtersOpen = false">
                  {{ $t("employees.filter.done") }}
                </button>
              </div>
            </div>
          </div>
          <button class="btn soft sm" type="button" data-test="open-assign" @click="openAssign">🔗 {{ $t("employees.assign.open") }}<template v-if="selected.length"> ({{ fInt(selected.length) }})</template></button>
          <button class="btn accent sm" type="button" @click="goImport">📥 {{ $t("employees.uploadExcel") }}</button>
          <button class="btn primary sm" type="button" @click="goAdd">➕ {{ $t("employees.add") }}</button>
        </div>

        <!-- B1 delete outcome. Explicit banners rather than a toast: HR needs to
             see WHY a delete was refused (e.g. a 409) long enough to act. -->
        <div v-if="deletedName" class="note info" data-test="deleted-note">
          ✓ {{ $t("employees.delete.done", { name: deletedName }) }}
        </div>
        <div v-if="deleteError" class="note" data-test="delete-error">
          {{ $t("employees.delete.errors." + deleteError) }}
        </div>

        <!-- bulk policy assignment -->
        <div v-if="showAssign" class="panel__body" style="border-bottom: 1px solid var(--v-line-2)" data-test="assign-panel">
          <div class="row between" style="margin-bottom: 8px">
            <b>🔗 {{ $t("employees.assign.title") }}</b>
            <button class="btn ghost sm" type="button" :disabled="assigning" @click="closeAssign">{{ $t("employees.assign.close") }}</button>
          </div>
          <div v-if="assignError" class="note danger" style="margin-bottom: 8px" role="alert">{{ assignError }}</div>
          <div class="row" style="gap: 14px; flex-wrap: wrap; align-items: flex-end">
            <label class="afield">
              <span class="sm">{{ $t("employees.assign.policy") }} *</span>
              <select v-model="assignPolicyId" :disabled="assigning" data-test="assign-policy">
                <option value="">{{ $t("employees.assign.policyPlaceholder") }}</option>
                <option v-for="p in policyOptions" :key="p.id" :value="p.id">{{ p.label }}</option>
              </select>
            </label>
            <label class="afield">
              <span class="sm">{{ $t("employees.assign.target") }}</span>
              <select v-model="assignMode" :disabled="assigning" data-test="assign-mode">
                <option value="selected">{{ $t("employees.assign.modeSelected", { n: selected.length }) }}</option>
                <option value="unassigned">{{ $t("employees.assign.modeUnassigned") }}</option>
              </select>
            </label>
            <label class="sm" style="display: flex; gap: 6px; align-items: center">
              <input type="checkbox" v-model="assignOverwrite" :disabled="assigning" />
              {{ $t("employees.assign.overwrite") }}
            </label>
            <button class="btn primary" type="button" :disabled="assigning" data-test="assign-submit" @click="submitAssign">
              {{ assigning ? $t("employees.assign.submitting") : $t("employees.assign.submit") }}
            </button>
          </div>
          <div v-if="assignResult" class="callout mt" data-test="assign-result">
            ✅ {{ $t("employees.assign.result", { assigned: fInt(assignResult.assigned), matched: fInt(assignResult.matched) }) }}
            <template v-if="assignResult.skipped.length">
              <div class="sm" style="margin-top: 6px"><b>{{ $t("employees.assign.skippedTitle", { n: fInt(assignResult.skipped.length) }) }}</b></div>
              <ul class="sm" style="margin: 4px 0 0; padding-inline-start: 18px">
                <li v-for="(s, i) in assignResult.skipped" :key="i">{{ s.name }} — {{ skipLabel(s.reason) }}</li>
              </ul>
            </template>
          </div>
        </div>

        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th style="width: 34px">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    :aria-label="$t('employees.assign.selectAll')"
                    data-test="select-all"
                    @change="onToggleAll"
                  />
                </th>
                <th>{{ $t("employees.col.employee") }}</th>
                <th>{{ $t("employees.col.nationalId") }}</th>
                <th>{{ $t("employees.col.dept") }}</th>
                <th>{{ $t("employees.col.policyNo") }}</th>
                <th>{{ $t("employees.col.hireDate") }}</th>
                <th>{{ $t("employees.col.status") }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(e, i) in rows" :key="e.id">
                <td>
                  <input
                    type="checkbox"
                    :checked="selected.indexOf(String(e.id)) !== -1"
                    :aria-label="$t('employees.assign.selectRow', { name: e.fullName })"
                    @change="selected = toggleId(selected, e.id)"
                  />
                </td>
                <td>
                  <div class="u">
                    <span class="avatar" :class="avatarClass(i)">{{ initials(e.fullName) }}</span>
                    {{ e.fullName }}
                  </div>
                </td>
                <td class="mono"><span dir="ltr">{{ gid(e.nationalId) }}</span></td>
                <td>{{ deptName(e.deptId, deptMap) }}</td>
                <td class="mono">{{ e.cardNumber }}</td>
                <td>{{ fDate(e.hireDate) }}</td>
                <td>
                  <span class="tag" :class="statusTag(e.status)">
                    <span class="dotc"></span> {{ statusLabel(e.status) }}
                  </span>
                </td>
                <td>
                  <div class="row" style="gap: 6px; justify-content: flex-end">
                    <button class="btn ghost sm" type="button" @click="openEmployee(e.id)">
                      {{ $t("employees.open") }}
                    </button>
                    <!-- Click-then-confirm inline, matching the admin portal's
                         DeleteButton: no window.confirm (it blocks the page and
                         cannot be translated or styled RTL). -->
                    <template v-if="confirmingId === String(e.id)">
                      <button
                        class="btn danger sm"
                        type="button"
                        :disabled="deletingId === String(e.id)"
                        :data-test="'delete-confirm-' + e.id"
                        @click="confirmDelete(e)"
                      >
                        {{ deletingId === String(e.id) ? $t("employees.delete.deleting") : $t("employees.delete.yes") }}
                      </button>
                      <button class="btn ghost sm" type="button" :disabled="deletingId === String(e.id)" @click="confirmingId = ''">
                        {{ $t("employees.delete.cancel") }}
                      </button>
                    </template>
                    <button
                      v-else
                      class="btn ghost sm"
                      type="button"
                      :title="$t('employees.delete.hint')"
                      :data-test="'delete-' + e.id"
                      @click="askDelete(e)"
                    >
                      🗑️ {{ $t("employees.delete.button") }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="8">
                  <div class="state">{{ $t("employees.empty") }}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="row between" style="padding: 11px 15px">
          <span class="muted xs">
            {{ $t("employees.showing") }} {{ fInt(range.start) }}–{{ fInt(range.end) }}
            {{ $t("employees.of") }} {{ fInt(range.total) }} {{ $t("employees.employeeUnit") }}
          </span>
          <div class="row" style="gap: 6px">
            <button class="btn ghost sm" type="button" :disabled="!canPrev" @click="prev">
              {{ $t("employees.prev") }}
            </button>
            <button class="btn ghost sm" type="button" :disabled="!canNext" @click="next">
              {{ $t("employees.next") }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet, apiPost, apiDelete } from "../../api/vivo/http";
import {
  statusTag,
  groupNationalId,
  initials,
  avatarClass,
  buildDeptMap,
  deptName,
  entityName,
  pageInfo,
  hasPrev,
  hasNext,
  assignablePolicies,
  buildAssignRequest,
  validateAssign,
  summarizeAssign,
  assignErrorKey,
  toggleId,
  toggleAll,
  buildListParams,
  activeFilterCount,
  deleteErrorKey,
  parseEmployeeQuery,
  assistantOutcome,
  askErrorKey,
} from "./employees-vm";

export default {
  name: "VivoEmployees",
  data() {
    return {
      loading: true,
      error: false,
      rows: [],
      meta: { page: 1, limit: 20, total: 0 },
      departments: [],
      deptMap: {},
      q: "",
      status: "",
      deptId: "",
      page: 1,
      limit: 20,
      selected: [],
      nameCache: {},
      showAssign: false,
      policyRows: [],
      assignPolicyId: "",
      assignMode: "selected",
      assignOverwrite: false,
      assigning: false,
      assignError: "",
      assignResult: null,
      ask: "",
      chat: {
        question: "",
        answer: null,
        asking: false,
        appliedFilters: false,
        emptyBank: false,
      },
      // B3 filter dropdown
      filtersOpen: false,
      // B1 delete
      confirmingId: "",
      deletingId: "",
      deletedName: "",
      deleteError: "",
    };
  },
  async mounted() {
    // Registered before the awaits: if the user navigates away while these are
    // in flight, beforeDestroy runs FIRST and the later addEventListener would
    // leak a handler bound to a destroyed component.
    document.addEventListener("click", this.onDocClick);
    try {
      const deps = await apiGet("/departments", { limit: 100 });
      this.departments = (deps && deps.data) || [];
      this.deptMap = buildDeptMap(this.departments);
      await this.load();
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocClick);
  },
  computed: {
    policyOptions() {
      return assignablePolicies(this.policyRows).map((p) => ({
        id: String(p.id),
        label: p.classLabel ? p.number + " · " + p.classLabel : p.number,
      }));
    },
    allSelected() {
      return this.rows.length > 0 && this.rows.every((r) => this.selected.indexOf(String(r.id)) !== -1);
    },
    statusChips() {
      return [
        { value: "", label: this.$t("employees.all") },
        { value: "active", label: this.$t("employees.status.active") },
        { value: "suspended", label: this.$t("employees.status.suspended") },
        { value: "left", label: this.$t("employees.status.left") },
      ];
    },
    range() {
      return pageInfo(this.meta, this.rows.length);
    },
    canPrev() {
      return hasPrev(this.meta);
    },
    canNext() {
      return hasNext(this.meta);
    },
    filterCount() {
      return activeFilterCount({ status: this.status, deptId: this.deptId, q: this.q });
    },
  },
  methods: {
    async load() {
      // Every filter is applied SERVER-side now (buildListParams). It used to
      // filter the fetched page in the browser because `status`/`deptId` were
      // rejected as unknown query properties — which made the footer count and
      // the pager wrong whenever a filter was on. `deptId` was added to
      // EmployeesQueryDto for exactly this.
      const params = buildListParams({
        page: this.page,
        limit: this.limit,
        q: this.q,
        status: this.status,
        deptId: this.deptId,
      });
      const res = await apiGet("/employees", params);
      const rows = (res && res.data) || [];
      this.rows = rows;
      rows.forEach((r) => {
        this.nameCache[String(r.id)] = r.fullName;
      });
      this.meta = (res && res.meta) || { page: this.page, limit: this.limit, total: rows.length };
    },
    toggleId(sel, id) {
      return toggleId(sel, id);
    },
    onToggleAll() {
      this.selected = toggleAll(
        this.selected,
        this.rows.map((r) => r.id),
      );
    },
    async openAssign() {
      this.showAssign = true;
      this.assignError = "";
      this.assignResult = null;
      if (this.selected.length === 0) this.assignMode = "unassigned";
      else this.assignMode = "selected";
      if (this.policyRows.length === 0) {
        try {
          const res = await apiGet("/policies", { limit: 100 });
          this.policyRows = (res && res.data) || [];
        } catch (e) {
          this.assignError = this.$t("employees.assign.apiErrors.policiesLoad");
        }
      }
    },
    closeAssign() {
      this.showAssign = false;
    },
    skipLabel(reason) {
      const key = "employees.assign.reasons." + reason;
      const label = this.$t(key);
      return label === key ? reason : label;
    },
    async submitAssign() {
      this.assignError = "";
      this.assignResult = null;
      const opts = {
        policyId: this.assignPolicyId,
        mode: this.assignMode,
        employeeIds: this.selected,
        overwrite: this.assignOverwrite,
      };
      const bad = validateAssign(opts);
      if (bad) {
        this.assignError = this.$t("employees.assign.errors." + bad);
        return;
      }
      this.assigning = true;
      try {
        const res = await apiPost("/employees/assign-policy", buildAssignRequest(opts));
        this.assignResult = summarizeAssign(res, this.nameCache);
        this.selected = [];
        await this.load();
      } catch (e) {
        this.assignError = this.$t("employees.assign.apiErrors." + assignErrorKey(e));
      } finally {
        this.assigning = false;
      }
    },
    async reload() {
      this.page = 1;
      try {
        await this.load();
      } catch (e) {
        this.error = true;
      }
    },
    setStatus(value) {
      this.status = value;
      this.reload();
    },
    clearFilters() {
      this.status = "";
      this.deptId = "";
      this.q = "";
      this.reload();
    },
    // Close the dropdown on any click outside it, so it cannot sit open over
    // the table (bound in mounted, removed in beforeDestroy).
    onDocClick() {
      this.filtersOpen = false;
    },
    askDelete(emp) {
      this.confirmingId = String(emp.id);
      this.deleteError = "";
      this.deletedName = "";
    },
    async confirmDelete(emp) {
      const id = String(emp.id);
      if (this.deletingId) return;
      this.deletingId = id;
      this.deleteError = "";
      try {
        await apiDelete("/employees/" + id);
        this.deletedName = emp.fullName;
        this.confirmingId = "";
        // Drop the row from the current selection: acting on a deleted id would
        // otherwise fail the next bulk assign.
        this.selected = this.selected.filter((s) => s !== id);
      } catch (e) {
        this.deleteError = deleteErrorKey(e);
        this.confirmingId = "";
      } finally {
        this.deletingId = "";
      }
      // Refresh OUTSIDE the delete's try: the delete already succeeded or
      // failed on its own terms. With the reload inside it, a 429 or a blip on
      // the follow-up GET turned a successful delete into "تعذّر حذف الموظف"
      // shown NEXT TO "تم الحذف", and the user retried a delete that had
      // already worked (getting a 404). Re-read rather than splice: the server
      // decides what the page holds now, since a row from the next page moves up.
      try {
        await this.load();
      } catch (e) {
        this.error = true;
      }
    },
    async prev() {
      if (!this.canPrev) return;
      this.page -= 1;
      try {
        await this.load();
      } catch (e) {
        this.error = true;
      }
    },
    async next() {
      if (!this.canNext) return;
      this.page += 1;
      try {
        await this.load();
      } catch (e) {
        this.error = true;
      }
    },
    async askBot() {
      const question = (this.ask || "").trim();
      if (!question || this.chat.asking) return;
      this.chat = {
        question: question,
        answer: null,
        asking: true,
        appliedFilters: false,
        emptyBank: false,
      };

      // B2: an EMPLOYEE question is answered here, from the employee list,
      // deterministically. /chatbot-qa/ask is a company FAQ bot — it cannot
      // answer «أحضر لي موظفي المبيعات الموقوفين» no matter what, and on this
      // deployment it answers everything with an empty string anyway (verified:
      // {answer:"",confidence:0,source:"fallback"} for every question, with an
      // empty company Q&A bank). Parsing the question into the filters we
      // already send needs no LLM, so it also works with the LLM disabled.
      const parsed = parseEmployeeQuery(question, this.departments);
      if (parsed.isEmployeeQuery) {
        try {
          this.status = parsed.status;
          this.deptId = parsed.deptId;
          this.q = parsed.q;
          this.page = 1;
          await this.load();
          const outcome = assistantOutcome(parsed, { total: this.meta.total });
          this.chat.answer = this.$t("employees.assistant." + outcome, {
            n: this.fInt(this.meta.total),
            status: parsed.status
              ? this.$t("employees.status." + parsed.status)
              : this.$t("employees.all"),
            dept: parsed.deptName || this.$t("employees.allDepartments"),
            name: parsed.q,
          });
          this.chat.appliedFilters = true;
        } catch (e) {
          this.chat.answer = this.$t("employees.assistant.errors." + askErrorKey(e));
        } finally {
          this.chat.asking = false;
          this.ask = "";
        }
        return;
      }

      try {
        const res = await apiPost("/chatbot-qa/ask", { question: question });
        const answer = res && res.answer;
        if (answer) {
          this.chat.answer = answer;
        } else {
          // An empty answer is NOT an error — it means nothing in the company's
          // Q&A bank matched. Saying which it is, and where to fix it, is the
          // difference between "the assistant is broken" and "nobody has filled
          // the bank yet".
          this.chat.answer = this.$t("employees.assistant.noMatch");
          this.chat.emptyBank = true;
        }
      } catch (e) {
        this.chat.answer = this.$t("employees.assistant.errors." + askErrorKey(e));
      } finally {
        this.chat.asking = false;
      }
    },
    openEmployee(id) {
      this.$router.push({ name: "employee-page", params: { id: String(id) } });
    },
    goAdd() {
      if (this.$route.name !== "employees-add") this.$router.push({ name: "employees-add" });
    },
    goImport() {
      if (this.$route.name !== "employees-import") this.$router.push({ name: "employees-import" });
    },
    deptLabel(d) {
      return entityName(d, this.$i18n.locale);
    },
    fInt(n) {
      return new Intl.NumberFormat("ar-EG", { numberingSystem: "latn" }).format(n || 0);
    },
    fDate(iso) {
      if (!iso) return "—";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
    statusLabel(status) {
      return this.$t("employees.status." + status);
    },
    statusTag(status) {
      return statusTag(status);
    },
    gid(nid) {
      return groupNationalId(nid);
    },
    initials(name) {
      return initials(name);
    },
    avatarClass(i) {
      return avatarClass(i);
    },
    deptName(deptId, map) {
      return deptName(deptId, map);
    },
  },
};
</script>

<style scoped>
.afield {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.afield select {
  padding: 8px 10px;
  border: 1px solid var(--v-line-2);
  border-radius: 8px;
  font: inherit;
  background: #fff;
  min-width: 200px;
}

/* B3 filters dropdown. `inset-inline-start: 0` not `left`, so the panel opens
   from the toggle in both RTL and LTR. */
.filters {
  position: relative;
}
.filters__panel {
  position: absolute;
  inset-inline-start: 0;
  top: calc(100% + 6px);
  z-index: 20;
  min-width: 250px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--v-line-2);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(16, 38, 44, 0.14);
}
.filters__row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.filters__row select {
  padding: 8px 10px;
  border: 1px solid var(--v-line-2);
  border-radius: 8px;
  font: inherit;
  background: #fff;
  width: 100%;
}
.filters__foot {
  border-top: 1px solid var(--v-line-2);
  padding-top: 9px;
}
</style>
